/**
 * API Response Normalization Utilities
 * Shared helpers for normalizing API response structures
 */

/**
 * Check if value is a non-null object (not array)
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Check if string represents a numeric ID
 */
export function isNumericId(value: string): boolean {
  return /^[0-9]+$/.test(String(value || '').trim())
}

/**
 * Normalize an API entity (handles both flattened and nested attribute structures)
 * Supports both API v4 format: { id, attributes: {...} }
 * And API v5/flattened format: { id, ...fields }
 */
export function normalizeEntity<T = Record<string, unknown>>(entity: unknown): T | null {
  if (!entity) return null
  if (!isRecord(entity)) return null

  // API v4 format: { id, attributes: {...} }
  if ('attributes' in entity && isRecord(entity.attributes)) {
    return { id: entity.id, ...entity.attributes } as T
  }

  // Already flattened format
  return entity as T
}

/**
 * Normalize a relation field (handles both data wrapper and direct formats)
 * API v4: { data: { id, attributes: {...} } }
 * API v5/flattened: { id, ...fields }
 */
export function normalizeRelation<T = Record<string, unknown>>(relation: unknown): T | null {
  if (!relation) return null
  if (!isRecord(relation)) return null

  // API v4 relation shape: { data: { ... } }
  if ('data' in relation && relation.data !== null) {
    return normalizeEntity<T>(relation.data)
  }

  // Direct entity format
  return normalizeEntity<T>(relation)
}

/**
 * Normalize an array relation field
 * API v4: { data: [{ id, attributes: {...} }, ...] }
 * API v5/flattened: [{ id, ...fields }, ...]
 */
export function normalizeRelationArray<T = Record<string, unknown>>(relation: unknown): T[] {
  if (!relation) return []

  // API v4 relation shape: { data: [...] }
  if (isRecord(relation) && 'data' in relation) {
    const data = relation.data
    if (!Array.isArray(data)) {
      // Single item wrapped
      const normalized = normalizeEntity<T>(data)
      return normalized ? [normalized] : []
    }
    return data.map((item) => normalizeEntity<T>(item)).filter((item): item is T => item !== null)
  }

  // Direct array format
  if (Array.isArray(relation)) {
    return relation.map((item) => normalizeEntity<T>(item)).filter((item): item is T => item !== null)
  }

  return []
}

/**
 * Coerce a value to a number ID if possible
 */
export function coerceToNumberId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

/**
 * Parse a value as a number, returning a default if parsing fails
 */
export function parseNumber(value: unknown, defaultValue = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return defaultValue
}

/**
 * Convert a date value to date-only string (YYYY-MM-DD) in LOCAL timezone.
 * Using local time methods (getFullYear/getMonth/getDate) ensures the date
 * matches what the user sees on their calendar, not the UTC date which can
 * differ by ±1 day for timezones far from UTC (e.g. UTC+3, UTC-8).
 */
export function toDateOnly(value: unknown): string {
  if (value == null) return ''
  const str = String(value).trim()
  if (!str) return ''
  // Already a date-only string (no time component) — return as-is
  if (!str.includes('T') && !str.endsWith('Z')) return str
  const date = new Date(str)
  if (isNaN(date.getTime())) return str.split('T')[0] // fallback for unparseable strings
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
